import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Modal, Form, Table, InputGroup, Button, Badge, Spinner, Row, Col } from 'react-bootstrap';

const SEARCH_MODES = [
    { id: 'descripcion', label: 'Descripción', placeholder: 'Buscar por Descripción...' },
    { id: 'filtro_descripc', label: 'Filtro Descripc', placeholder: 'Filtrar por palabras en la Descripción...' },
    { id: 'codigo', label: 'Código', placeholder: 'Buscar por Código...' },
    { id: 'rubro', label: 'Rubro', placeholder: 'Buscar por Rubro...' },
    { id: 'familia', label: 'Familia', placeholder: 'Buscar por Familia...' },
    { id: 'marca', label: 'Marca', placeholder: 'Buscar por Marca...' },
    { id: 'cod_barras', label: 'Cód Barras', placeholder: 'Buscar por Código de Barras...' },
    { id: 'variante_equival', label: 'Variante/Equival', placeholder: 'Buscar por Variante / Equivalente...' },
];

const GlobalArticleSearchModal = ({ 
    show, 
    onHide, 
    onSelectArticle, 
    initialSearch = '', 
    defaultSearchMode = 'codigo',
    onOpenImageModal,
    pricesByArtic = {},
    rubros = []
}) => {
    const [search, setSearch] = useState('');
    const [searchMode, setSearchMode] = useState(() => {
        try {
            return localStorage.getItem('sigecom_search_mode') || defaultSearchMode || 'descripcion';
        } catch {
            return defaultSearchMode || 'descripcion';
        }
    });
    const [articles, setArticles] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    const debounceTimerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const abortControllerRef = useRef(null);
    const isFetchingRef = useRef(false);
    const currentPageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const searchTermRef = useRef('');
    const searchModeRef = useRef(defaultSearchMode || 'codigo');
    const pricesByArticRef = useRef(pricesByArtic);
    const rubrosMapRef = useRef({});
    const isNavigatingListRef = useRef(false);

    useEffect(() => {
        searchModeRef.current = searchMode;
    }, [searchMode]);

    useEffect(() => {
        pricesByArticRef.current = pricesByArtic;
    }, [pricesByArtic]);

    useEffect(() => {
        const map = {};
        if (Array.isArray(rubros)) {
            rubros.forEach(r => {
                if (r && r.c_rubro) {
                    map[r.c_rubro] = r.d_rubro || `Rubro ${r.c_rubro}`;
                }
            });
        }
        rubrosMapRef.current = map;
    }, [rubros]);

    // Mapear un artículo crudo de la base de datos al formato estándar del cotizador
    const mapArticle = useCallback((art) => {
        const currentPrices = pricesByArticRef.current || {};
        const rubrosMap = rubrosMapRef.current || {};
        const listPrice = currentPrices[art.c_artic];
        const rubroDesc = rubrosMap[art.c_rubro] || (art.c_rubro ? `Rubro ${art.c_rubro}` : 'Sin Rubro');
        return {
            id: art.c_artic,
            name: art.d_artic,
            price: listPrice !== undefined && listPrice !== null ? parseFloat(listPrice) : parseFloat(art.i_conta || 0),
            bulto: art.q_unxbu !== undefined && art.q_unxbu !== null ? parseFloat(art.q_unxbu) : 1,
            q_medid: art.q_medid !== undefined && art.q_medid !== null ? parseFloat(art.q_medid) : 1,
            description: art.d_arti2 || art.d_artic,
            notes: "",
            c_iva: art.c_iva,
            c_barra: art.c_barra || "",
            c_situa: art.c_situa,
            c_vendev: (art.c_vendev && art.c_vendev.trim()) ? art.c_vendev : "V",
            rubroId: art.c_rubro,
            rubroName: rubroDesc,
            t_artic: art.t_artic,
            i_suger: art.i_suger,
            c_moned: art.c_moned,
            c_descli: art.c_descli,
            c_varian: art.c_varian,
            c_ubica: art.c_ubica,
            d_pasil: art.d_pasil,
            d_estan: art.d_estan,
            d_posic: art.d_posic,
            d_imagen: art.d_imagen,
            d_pathimg: art.d_pathimg,
            d_undisky: art.d_undisky,
            c_unmed: art.c_unmed,
            c_ingbr: art.c_ingbr,
            c_impuc: art.c_impuc,
            l_trazar: art.l_trazar,
            q_pesuni: art.q_pesuni
        };
    }, []);

    // Carga inicial o búsqueda nueva (Página 1)
    const loadInitialSearch = useCallback(async (term, mode = searchModeRef.current) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        isFetchingRef.current = true;
        setIsLoading(true);
        currentPageRef.current = 1;
        searchTermRef.current = term;
        setSelectedIndex(0);

        try {
            const params = {
                page: 1,
                page_size: 100,
                search_mode: mode
            };
            if (term && term.trim() !== '') {
                params.search = term.trim();
            }

            const res = await axios.get('http://127.0.0.1:8000/api/articulos/', { 
                params,
                signal: controller.signal
            });
            const data = res.data;
            const rawResults = data.results || (Array.isArray(data) ? data : []);
            const more = Boolean(data.next) || rawResults.length === 100;
            const count = data.count !== undefined ? data.count : rawResults.length;

            const mapped = rawResults.map(mapArticle);

            setArticles(mapped);
            hasMoreRef.current = more;
            setHasMore(more);
            setTotalCount(count);
            setSelectedIndex(0);

            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
            return mapped;
        } catch (err) {
            if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
                return [];
            }
            console.error('Error buscando artículos:', err);
            setArticles([]);
            hasMoreRef.current = false;
            setHasMore(false);
            setTotalCount(0);
            setSelectedIndex(0);
            return [];
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [mapArticle]);

    // Carga de página siguiente (Scroll infinito)
    const loadNextPage = useCallback(async () => {
        if (isFetchingRef.current || !hasMoreRef.current) return;
        isFetchingRef.current = true;
        setIsLoadingMore(true);

        const nextPage = currentPageRef.current + 1;

        try {
            const params = {
                page: nextPage,
                page_size: 100,
                search_mode: searchModeRef.current
            };
            const currentTerm = searchTermRef.current;
            if (currentTerm && currentTerm.trim() !== '') {
                params.search = currentTerm.trim();
            }

            const res = await axios.get('http://127.0.0.1:8000/api/articulos/', { params });
            const data = res.data;
            const rawResults = data.results || (Array.isArray(data) ? data : []);
            const more = Boolean(data.next) || rawResults.length === 100;
            const count = data.count !== undefined ? data.count : rawResults.length;

            const mapped = rawResults.map(mapArticle);

            setArticles(prev => [...prev, ...mapped]);
            currentPageRef.current = nextPage;
            hasMoreRef.current = more;
            setHasMore(more);
            setTotalCount(count);
        } catch (err) {
            if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') {
                return;
            }
            console.error('Error cargando más artículos:', err);
            hasMoreRef.current = false;
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
            isFetchingRef.current = false;
        }
    }, [mapArticle]);

    // Manejar cambio de modo de búsqueda
    const handleModeChange = (modeId) => {
        setSearchMode(modeId);
        searchModeRef.current = modeId;
        isNavigatingListRef.current = false;

        // 1. Guardar en localStorage para recordar en la sesión actual y entre aperturas del modal
        try {
            localStorage.setItem('sigecom_search_mode', modeId);
            const userDataStr = sessionStorage.getItem('tabUserData') || localStorage.getItem('userData');
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                if (userData && userData.empresa_activa) {
                    userData.empresa_activa.default_search_mode = modeId;
                    sessionStorage.setItem('tabUserData', JSON.stringify(userData));
                }
            }
        } catch (e) {
            console.error('Error guardando search_mode local:', e);
        }

        // 2. Guardar en backend (sges020.n_posart) en la base de datos de la empresa
        axios.post('http://127.0.0.1:8000/api/empresa/guardar-posart/', { search_mode: modeId })
            .catch(err => console.warn('No se pudo guardar posart en backend:', err));

        loadInitialSearch(search, modeId);
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    // Ciclo de vida: Disparar carga cuando show pasa a true
    useEffect(() => {
        if (show) {
            const term = initialSearch || '';
            let savedMode = null;
            try {
                savedMode = localStorage.getItem('sigecom_search_mode');
            } catch {}
            const mode = savedMode || defaultSearchMode || searchMode || 'descripcion';
            setSearch(term);
            setSearchMode(mode);
            searchModeRef.current = mode;
            isNavigatingListRef.current = false;
            loadInitialSearch(term, mode);
            setTimeout(() => {
                if (searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 100);
        } else {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            setArticles([]);
            setSearch('');
            searchTermRef.current = '';
            currentPageRef.current = 1;
            hasMoreRef.current = true;
            isNavigatingListRef.current = false;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // Manejar cambios en el texto de búsqueda con debounce de 300ms
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        isNavigatingListRef.current = false;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            loadInitialSearch(val, searchModeRef.current);
        }, 300);
    };

    // Manejador del scroll infinito (cuando se acerca al fondo)
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 200) {
            if (hasMoreRef.current && !isFetchingRef.current && !isLoading && !isLoadingMore) {
                loadNextPage();
            }
        }
    };

    // Mantener la fila seleccionada visible en el scroll
    useEffect(() => {
        if (scrollContainerRef.current) {
            const selectedRow = scrollContainerRef.current.querySelector('tr.table-primary');
            if (selectedRow) {
                selectedRow.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex]);

    const handleKeyDown = async (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            isNavigatingListRef.current = true;
            setSelectedIndex(prev => Math.min(prev + 1, articles.length - 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            isNavigatingListRef.current = true;
            setSelectedIndex(prev => Math.max(prev - 1, 0));
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            const currentVal = (e.target.value !== undefined ? e.target.value : search).trim();

            // CASO 1: Si el usuario navegó explícitamente con las flechas, Enter selecciona el ítem resaltado
            if (isNavigatingListRef.current) {
                if (articles.length > 0) {
                    const targetIdx = selectedIndex >= 0 && selectedIndex < articles.length ? selectedIndex : 0;
                    const targetArticle = articles[targetIdx];
                    if (targetArticle) {
                        onSelectArticle(targetArticle);
                        onHide();
                    }
                }
                return;
            }

            // CASO 2: El usuario está escribiendo en el buscador y aprieta Enter (ej: "grapa" o un código de barras)
            // Enter aquí significa: BUSCAR inmediatamente lo que se escribió
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }

            const results = await loadInitialSearch(currentVal, searchModeRef.current);

            // Únicamente si es código o código de barras y encontró exactamente 1 resultado (modo lector de barras), auto-selecciona
            if ((searchModeRef.current === 'codigo' || searchModeRef.current === 'cod_barras') && results && results.length === 1) {
                onSelectArticle(results[0]);
                onHide();
            } else {
                // En modo descripción o si hay varios resultados o 0 resultados: NO auto-seleccionar nada, dejar que el usuario vea la grilla
                isNavigatingListRef.current = false;
                setSelectedIndex(0);
            }
        }
    };

    const currentModeObj = SEARCH_MODES.find(m => m.id === searchMode) || SEARCH_MODES[2];

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="bg-primary text-white py-2">
                <Modal.Title className="fs-5"><i className="bi bi-search me-2"></i>Buscador Global de Artículos</Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="p-0 d-flex flex-column" style={{ height: '75vh' }}>
                {/* SECCIÓN DE BÚSQUEDA Y FILTROS: Diseño limpio acorde al legacy */}
                <div className="p-3 bg-light border-bottom flex-shrink-0">
                    <Row className="g-3 align-items-center">
                        {/* Columna Izquierda: Input de Búsqueda */}
                        <Col lg={5} md={12}>
                            <InputGroup className="shadow-sm">
                                <InputGroup.Text className="bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    ref={searchInputRef}
                                    placeholder={currentModeObj.placeholder}
                                    value={search}
                                    className="border-start-0 shadow-none ps-0"
                                    onChange={handleSearchChange}
                                    onKeyDown={handleKeyDown}
                                />
                                {search && (
                                    <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0 bg-white"
                                        onClick={() => {
                                            setSearch('');
                                            loadInitialSearch('', searchMode);
                                            if (searchInputRef.current) searchInputRef.current.focus();
                                        }}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>

                        {/* Columna Derecha: Radio Buttons (2 filas x 4 columnas idéntico a legacy) */}
                        <Col lg={7} md={12}>
                            <div className="bg-white px-3 py-2 border rounded shadow-sm">
                                <Row className="g-2">
                                    {SEARCH_MODES.map(mode => (
                                        <Col xs={6} sm={3} key={mode.id}>
                                            <Form.Check
                                                type="radio"
                                                id={`search-mode-${mode.id}`}
                                                name="globalSearchMode"
                                                label={<span className="ms-1">{mode.label}</span>}
                                                checked={searchMode === mode.id}
                                                onChange={() => handleModeChange(mode.id)}
                                                className="user-select-none text-nowrap mb-0 d-flex align-items-center"
                                                style={{ fontSize: '0.84rem', cursor: 'pointer' }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </Col>
                    </Row>

                    {totalCount > 0 && !isLoading && (
                        <div className="text-muted small mt-2 px-1 d-flex justify-content-between align-items-center">
                            <span>Mostrando {articles.length} de {totalCount} artículos encontrados</span>
                            {hasMore && <span className="text-primary fw-semibold"><i className="bi bi-arrow-down me-1"></i>Haga scroll para cargar más</span>}
                        </div>
                    )}
                </div>

                {/* CONTENEDOR CON SCROLL DEDICADO: Tabla de Artículos */}
                <div 
                    ref={scrollContainerRef}
                    className="flex-grow-1 bg-white" 
                    onScroll={handleScroll}
                    style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
                >
                    {isLoading ? (
                        <div className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: '300px' }}>
                            <Spinner animation="border" variant="primary" className="mb-2" />
                            <span className="text-muted">Buscando artículos en base de datos...</span>
                        </div>
                    ) : articles.length > 0 ? (
                        <>
                            <Table hover size="sm" className="mb-0 align-middle">
                                <thead className="table-dark sticky-top" style={{ top: 0, zIndex: 10 }}>
                                    <tr>
                                        <th style={{ width: '120px' }} className="ps-3">Código</th>
                                        <th>Descripción</th>
                                        <th>Rubro</th>
                                        <th style={{ width: '150px' }}>Precio</th>
                                        <th style={{ width: '110px' }} className="text-center pe-3">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {articles.map((a, idx) => (
                                        <tr 
                                            key={`${a.id}-${idx}`}
                                            className={idx === selectedIndex ? "table-primary" : ""}
                                            onClick={() => {
                                                setSelectedIndex(idx);
                                                isNavigatingListRef.current = true;
                                            }}
                                            onDoubleClick={() => {
                                                onSelectArticle(a);
                                                onHide();
                                            }}
                                            onTouchEnd={(e) => {
                                                const now = Date.now();
                                                const lastTap = e.currentTarget._lastTap || 0;
                                                if (now - lastTap < 300) {
                                                    onSelectArticle(a);
                                                    onHide();
                                                }
                                                e.currentTarget._lastTap = now;
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td className="fw-bold ps-3">{a.id}</td>
                                            <td>{a.name}</td>
                                            <td>
                                                <Badge bg="info" className="text-dark fw-normal">{a.rubroName}</Badge>
                                            </td>
                                            <td className="fw-bold">${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2 }).format(a.price || 0)}</td>
                                            <td className="text-center pe-3">
                                                <div className="d-flex align-items-center justify-content-center gap-1">
                                                    {onOpenImageModal && (
                                                        <Button 
                                                            variant="outline-info" 
                                                            size="sm" 
                                                            className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                            style={{ width: '32px', height: '32px', padding: 0 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onOpenImageModal(a);
                                                            }}
                                                            title="Ver imagen del artículo"
                                                        >
                                                            <i className="bi bi-eye"></i>
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                        style={{ width: '32px', height: '32px', padding: 0 }}
                                                        onClick={() => {
                                                            onSelectArticle(a);
                                                            onHide();
                                                        }}
                                                        title="Agregar artículo"
                                                    >
                                                        <i className="bi bi-plus-lg"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {isLoadingMore && (
                                <div className="text-center py-3 bg-light border-top d-flex align-items-center justify-content-center">
                                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                    <span className="text-muted small">Cargando más artículos...</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center text-muted py-5" style={{ minHeight: '300px' }}>
                            <div className="mb-3">
                                <i className="bi bi-search text-secondary" style={{ fontSize: '3.5rem', opacity: 0.3 }}></i>
                            </div>
                            <h5>No se encontraron artículos</h5>
                            <p className="small">Intente con otro término o verifique la ortografía.</p>
                        </div>
                    )}
                </div>
            </Modal.Body>
            
            <Modal.Footer className="bg-light py-2">
                <Button variant="secondary" size="sm" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GlobalArticleSearchModal;
